import React, {useState, useEffect, useMemo, useRef} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {format} from 'date-fns';
import {SupabaseService} from '../../services/supabaseService';
import {FolderService} from '../../services/folderService';
import FolderList from '../Folders/FolderList';
import FolderAssignmentModal from '../Folders/FolderAssignmentModal';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiSearch, FiCalendar, FiBarChart3, FiEye, FiDownload, FiTrash2, FiX, FiAlertCircle, FiCheck, FiChevronLeft, FiChevronRight, FiFolder} = FiIcons;

const SearchHistory = ({searches, onLoadSearch, user, onSearchDeleted}) => {
  const [exporting, setExporting] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [visibleStatusCounts, setVisibleStatusCounts] = useState({}); // { [searchId]: {new, submitted, live} }
  const [filteredSearches, setFilteredSearches] = useState([]);
  const [folderAssignmentModalOpen, setFolderAssignmentModalOpen] = useState(false);
  const [searchToAssign, setSearchToAssign] = useState(null);
  const [foldersUpdated, setFoldersUpdated] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  
  const itemsPerPage = 15;

  // Reset to first page when folder selection changes to avoid empty pages
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFolderId]);

  // Filter searches by selected folder
  useEffect(() => {
    const filterSearches = async () => {
      let filtered = [...searches];
      
      // Apply folder filter if selected
      if (selectedFolderId) {
        try {
          // Get searches in the selected folder
          const {data, error} = await SupabaseService.supabase
            .from('folder_searches')
            .select('search_id')
            .eq('folder_id', selectedFolderId);
          
          if (error) throw error;
          
          const searchIds = data.map(item => item.search_id);
          // Filter the searches array
          filtered = filtered.filter(search => searchIds.includes(search.id));
        } catch (error) {
          console.error('Error filtering searches by folder:', error);
        }
      }
      
      // Apply text search filter if provided
      if (searchFilter.trim()) {
        const searchTerm = searchFilter.toLowerCase().trim();
        filtered = filtered.filter(search => 
          search.keyword.toLowerCase().includes(searchTerm)
        );
      }
      
      setFilteredSearches(filtered);
    };
    
    filterSearches();
  }, [searches, selectedFolderId, foldersUpdated, searchFilter]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredSearches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSearches = filteredSearches.slice(startIndex, endIndex);

  const visibleSearchIds = useMemo(() => paginatedSearches.map(s => s.id), [paginatedSearches]);

  const lastFetchedIdsRef = useRef([]);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;

    // Deep equality check to avoid redundant fetches
    const prev = lastFetchedIdsRef.current;
    const next = visibleSearchIds;
    const sameLength = prev.length === next.length;
    const sameOrder = sameLength && prev.every((v, i) => v === next[i]);
    if (sameOrder) {
      console.log('Skipping fetchStatusesForVisible — visibleSearchIds unchanged.');
      return;
    }

    // Log for testing
    console.log('Visible search IDs changed:', next);

    // Debounce fetch
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        if (!Array.isArray(next) || next.length === 0) {
          setVisibleStatusCounts({});
          lastFetchedIdsRef.current = next;
          return;
        }
        const statusListsBySearch = await SupabaseService.fetchStatusesForVisible(user.id, next);
        const bySearchCounts = {};
        next.forEach(sid => {
          const statuses = statusListsBySearch[sid] || [];
          const counts = { new: 0, submitted: 0, live: 0 };
          statuses.forEach(st => { counts[st] = (counts[st] || 0) + 1; });
          bySearchCounts[sid] = counts;
        });
        setVisibleStatusCounts(bySearchCounts);
        lastFetchedIdsRef.current = next;
      } catch (e) {
        // Non-blocking
      }
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [user?.id, visibleSearchIds]);

  // If filters reduce results and current page is out of range, reset to 1
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const exportAllToCSV = async () => {
    if (exporting || filteredSearches.length === 0) return;
    setExporting(true);

    try {
      // Create CSV headers
      const headers = ['Keyword', 'Link Type', 'Link Title', 'Link URL', 'Link Status'];
      // Create CSV rows
      const csvRows = [];
      csvRows.push(headers.join(','));

      // Process all searches
      for (const search of filteredSearches) {
        const keyword = search.keyword || '';
        if (search.results && Array.isArray(search.results)) {
          // Get statuses for this search if available
          const statuses = search.status_counts || {};

          // Process each result in the search
          search.results.forEach((result, index) => {
            const linkType = result.category || 'Other';
            const linkTitle = `"${(result.title || '').replace(/"/g, '""')}"`;  // Escape quotes
            const linkUrl = result.url || '';
            // Get status for this result (default to "new" if not found)
            // We're using the index as the result ID since that's how it's stored
            const resultStatus = statuses[index] || 'new';

            // Add row to CSV
            csvRows.push([
              `"${keyword.replace(/"/g, '""')}"`,
              linkType,
              linkTitle,
              linkUrl,
              resultStatus
            ].join(','));
          });
        }
      }

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create and download file
      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `backlinks_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting searches to CSV:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteClick = (search) => {
    setSearchToDelete(search);
    setDeleteConfirmVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!searchToDelete) return;
    setIsDeleting(true);

    try {
      // Call Supabase service to delete the search
      const success = await SupabaseService.deleteSearch(user.id, searchToDelete.id);

      if (success) {
        setDeleteSuccess(true);
        // Notify parent component that a search was deleted
        if (onSearchDeleted) {
          onSearchDeleted(searchToDelete.id);
        }

        // Close the confirmation after showing success briefly
        setTimeout(() => {
          setDeleteConfirmVisible(false);
          setDeleteSuccess(false);
          setSearchToDelete(null);
        }, 1500);
      }
    } catch (error) {
      console.error('Error deleting search:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmVisible(false);
    setSearchToDelete(null);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleAssignToFolder = (search) => {
    setSearchToAssign(search);
    setFolderAssignmentModalOpen(true);
  };

  const handleFolderAssignmentComplete = () => {
    // Trigger a refresh of the folder contents
    setFoldersUpdated(prev => !prev);
  };

  // Helper function to get status counts
  const getStatusCounts = (search) => {
    const counts = visibleStatusCounts[search.id];
    if (!counts) return { new: 0, submitted: 0, live: 0 };
    return { new: counts.new || 0, submitted: counts.submitted || 0, live: counts.live || 0 };
  };

  if (searches.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 dark:bg-gray-700">
          <SafeIcon icon={FiSearch} className="w-8 h-8 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-white">No history yet</h3>
        <p className="text-gray-600 dark:text-gray-400">Start by searching for backlink opportunities</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-white">Search History</h2>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
          <p className="text-gray-600 dark:text-gray-400">View and manage your previous backlink searches</p>
          <button
            onClick={exportAllToCSV}
            disabled={exporting}
            className="flex items-center mt-2 sm:mt-0 text-[#D59A4C] hover:text-[#B58C54] font-medium transition-colors"
          >
            <SafeIcon icon={exporting ? FiSearch : FiDownload} className={`w-4 h-4 mr-1 ${exporting ? 'animate-spin' : ''}`} />
            {exporting ? 'Exporting...' : 'Export to CSV'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Folders Sidebar */}
        <div className="col-span-12 md:col-span-3">
          <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 md:mb-0 dark:bg-gray-800">
            <FolderList
              user={user}
              onFolderSelect={setSelectedFolderId}
              selectedFolderId={selectedFolderId}
              onFolderUpdated={() => setFoldersUpdated(prev => !prev)}
            />
            
            {/* Search filter input */}
            <div className="mt-4 pt-4 border-t dark:border-gray-700">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search History</div>
              <div className="relative">
                <input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Filter by keyword..."
                  className="w-full px-3 py-2 pl-9 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <SafeIcon 
                  icon={FiSearch} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Search Results */}
        <div className="col-span-12 md:col-span-9">
          {filteredSearches.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center dark:bg-gray-800">
              <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 dark:bg-gray-700">
                <SafeIcon icon={FiFolder} className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-white">No searches found</h3>
              <p className="text-gray-600 dark:text-gray-400">Try adjusting your filters to see more results</p>
            </div>
          ) : (
            <div className="grid gap-6">
              {paginatedSearches.map((search) => {
                const statusCounts = getStatusCounts(search);
                return (
                  <motion.div
                    key={search.id}
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow dark:bg-gray-800"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <div className="bg-[#D59A4C20] p-2 rounded-lg mr-3">
                            <SafeIcon icon={FiSearch} className="w-5 h-5 text-[#D59A4C]" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            "{search.keyword}"
                          </h3>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 mb-4 dark:text-gray-400">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />
                          <span className="mr-4">
                            {format(new Date(search.created_at), 'MMM d, yyyy HH:mm')}
                          </span>
                          <SafeIcon icon={FiBarChart3} className="w-4 h-4 mr-1" />
                          <span>
                            {search.results?.length || 0} opportunities found
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-3">
                          {search.results && Object.entries(
                            search.results.reduce((acc, result) => {
                              const category = result.category || 'Other';
                              acc[category] = (acc[category] || 0) + 1;
                              return acc;
                            }, {})
                          ).map(([category, count]) => (
                            <span
                              key={category}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm dark:bg-gray-700 dark:text-gray-300"
                            >
                              {category}: {count}
                            </span>
                          ))}
                        </div>

                        {/* Status badges */}
                        <div className="flex gap-2 mt-2">
                          {!visibleStatusCounts[search.id] && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs font-medium dark:bg-gray-700 dark:text-gray-300">
                              loading
                            </span>
                          )}
                          {statusCounts.new > 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium dark:bg-green-900/30 dark:text-green-400">
                              {statusCounts.new} new
                            </span>
                          )}
                          {statusCounts.submitted > 0 && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-medium dark:bg-orange-900/30 dark:text-orange-400">
                              {statusCounts.submitted} submitted
                            </span>
                          )}
                          {statusCounts.live > 0 && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-md text-xs font-medium dark:bg-yellow-900/30 dark:text-yellow-400">
                              {statusCounts.live} live
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons - Stack vertically on mobile */}
                      <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:gap-2">
                        <button
                          onClick={() => onLoadSearch(search)}
                          className="flex items-center justify-center px-2 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] transition-colors"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4 mr-2" />
                          <span>View</span>
                        </button>
                        <button
                          onClick={() => handleAssignToFolder(search)}
                          className="flex items-center justify-center px-2 py-1.5 sm:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 no-border"
                          title="Assign to folder"
                        ><SafeIcon icon={FiFolder} className="w-4 h-4 mr-2" /></button>
                        <button
                          onClick={() => handleDeleteClick(search)}
                          className="flex items-center justify-center px-3 py-1.5 sm:py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors dark:hover:bg-red-900/30 no-border"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          <span className="ml-1 sm:hidden">Delete</span>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Next
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmVisible && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{scale: 0.9, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              exit={{scale: 0.9, opacity: 0}}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full dark:bg-gray-800"
            >
              <div className="p-6">
                {deleteSuccess ? (
                  <div className="text-center py-4">
                    <div className="bg-green-100 p-3 rounded-full mx-auto mb-4 w-16 h-16 flex items-center justify-center dark:bg-green-900/30">
                      <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 dark:text-white">Deleted Successfully</h3>
                    <p className="text-gray-600 dark:text-gray-400">The search has been removed from your history.</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="bg-red-100 p-2 rounded-full mr-3 dark:bg-red-900/30">
                          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 dark:text-red-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Delete Confirmation</h3>
                      </div>
                      <button
                        onClick={handleCancelDelete}
                        className="p-1 rounded-full hover:bg-gray-100 transition-colors dark:hover:bg-gray-700"
                      >
                        <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      </button>
                    </div>

                    <div className="py-4">
                      <p className="text-gray-600 mb-2 dark:text-gray-400">
                        Are you sure you want to delete these link opportunities?
                      </p>
                      <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg dark:bg-gray-700 dark:text-gray-400">
                        <span className="font-medium">Keyword:</span> {searchToDelete?.keyword}
                      </p>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={handleCancelDelete}
                        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmDelete}
                        disabled={isDeleting}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Folder Assignment Modal */}
      <FolderAssignmentModal
        isOpen={folderAssignmentModalOpen}
        search={searchToAssign}
        onClose={() => {
          setFolderAssignmentModalOpen(false);
          setSearchToAssign(null);
        }}
        onAssigned={handleFolderAssignmentComplete}
        user={user}
      />
    </div>
  );
};

export default SearchHistory;